import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { api } from '../Services/api'
import { KEYS, readJSON, removeKey, writeJSON } from '../Utils/storage'
import { norm, slugify } from '../Utils/text'
import { setPersonProps } from '../Utils/analytics'

/* What this browser follows, kept in localStorage without any account
   (spec 5.7, 5.6):
   - topics: [{ key, type: 'topic' | 'keyword', slug?, title }], newest first.
     A topic is an existing Instapres topic (by slug); a keyword is the user's
     own text ("Željezničar"), matched in headlines by the API.
   - sources: followed source slugs; their headlines win on cards. */
const FollowContext = createContext(null)

const topicKey = (slug) => `t:${slug}`
const keywordKey = (title) => `k:${norm(title)}`

function cleanTopics(value) {
  if (!Array.isArray(value)) return []
  return value
    .filter(
      (t) =>
        t && typeof t.title === 'string' && (t.type === 'keyword' || (t.type === 'topic' && t.slug))
    )
    .map((t) => ({ ...t, key: t.type === 'topic' ? topicKey(t.slug) : keywordKey(t.title) }))
}

function cleanSources(value) {
  return Array.isArray(value) ? value.filter((s) => typeof s === 'string') : []
}

// Same topic under another name: "zeljeznicar" == "Željezničar", or a
// keyword typed exactly as an existing topic's name.
function findDuplicate(list, entry) {
  const title = norm(entry.title)
  return list.find((t) => t.key === entry.key || norm(t.title) === title)
}

/* The old site kept "Moje teme" in a server profile (redux-persist token).
   Bring those topics into this browser once. */
async function legacyTopics() {
  let token = null
  try {
    const root = JSON.parse(window.localStorage.getItem('persist:root') || 'null')
    token = root && JSON.parse(root.myTopics || 'null')?.token
  } catch (e) {
    token = null
  }
  if (!token) return []
  try {
    const items = await api.legacyMyTopics(token)
    return (Array.isArray(items) ? items : []).map((it) =>
      it.type === 'topic'
        ? {
            key: topicKey(slugify(it.label)),
            type: 'topic',
            slug: slugify(it.label),
            title: it.label,
          }
        : { key: keywordKey(it.label), type: 'keyword', title: it.label }
    )
  } catch (e) {
    if (e?.response?.status === 401) return [] // profile gone: nothing to move
    throw e
  }
}

export function FollowProvider({ children }) {
  const [topics, setTopicsState] = useState(() => cleanTopics(readJSON(KEYS.myTopics, [])))
  const [sources, setSourcesState] = useState(() =>
    cleanSources(readJSON(KEYS.followedSources, []))
  )
  // Latest lists, so add/remove can answer synchronously (state updaters run later).
  const topicsRef = useRef(topics)
  const sourcesRef = useRef(sources)

  const setTopics = useCallback((next) => {
    topicsRef.current = next
    setTopicsState(next)
    writeJSON(KEYS.myTopics, next)
  }, [])
  const setSources = useCallback((next) => {
    sourcesRef.current = next
    setSourcesState(next)
    writeJSON(KEYS.followedSources, next)
  }, [])

  // What's followed, on the person in analytics (analytics spec 5.3).
  useEffect(() => {
    const custom = topics.filter((x) => x.type === 'keyword').length
    setPersonProps({
      followed_topics_count: topics.length - custom,
      custom_topics_count: custom,
      followed_sources_count: sources.length,
    })
  }, [topics, sources])

  // Another tab changed what's followed.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === KEYS.myTopics) {
        topicsRef.current = cleanTopics(readJSON(KEYS.myTopics, []))
        setTopicsState(topicsRef.current)
      }
      if (e.key === KEYS.followedSources) {
        sourcesRef.current = cleanSources(readJSON(KEYS.followedSources, []))
        setSourcesState(sourcesRef.current)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  useEffect(() => {
    if (readJSON(KEYS.migratedProfile, false)) return
    let cancelled = false
    legacyTopics()
      .then((entries) => {
        if (cancelled) return
        const merged = [...topicsRef.current]
        entries.forEach((e) => !findDuplicate(merged, e) && merged.push(e))
        if (merged.length !== topicsRef.current.length) setTopics(merged)
        writeJSON(KEYS.migratedProfile, true)
        removeKey('persist:root')
      })
      .catch(() => {}) // network error: try again on the next visit
    return () => {
      cancelled = true
    }
  }, [setTopics])

  /* Add at the front (spec 5.7). Returns { added, key }: an existing entry
     isn't added again, its key is returned so the page can highlight it. */
  const add = useCallback(
    (entry) => {
      const existing = findDuplicate(topicsRef.current, entry)
      if (existing) return { added: false, key: existing.key }
      setTopics([entry, ...topicsRef.current])
      return { added: true, key: entry.key }
    },
    [setTopics]
  )

  const addTopic = useCallback(
    ({ slug, title }) => add({ key: topicKey(slug), type: 'topic', slug, title }),
    [add]
  )

  const addKeyword = useCallback(
    (text) => {
      const title = String(text || '')
        .replace(/\s+/g, ' ')
        .trim()
      return add({ key: keywordKey(title), type: 'keyword', title })
    },
    [add]
  )

  /* Returns { item, index } for an undo, or null. */
  const removeTopic = useCallback(
    (key) => {
      const index = topicsRef.current.findIndex((t) => t.key === key)
      if (index === -1) return null
      const item = topicsRef.current[index]
      setTopics(topicsRef.current.filter((t) => t.key !== key))
      return { item, index }
    },
    [setTopics]
  )

  const restoreTopic = useCallback(
    ({ item, index }) => {
      if (findDuplicate(topicsRef.current, item)) return
      const next = [...topicsRef.current]
      next.splice(Math.min(index, next.length), 0, item)
      setTopics(next)
    },
    [setTopics]
  )

  /* A followed topic was merged into another (spec 9.7a, the API resolves
     its old slug to the new one): follow the new one in its place. Returns
     the entry's key now, or null when it wasn't followed. */
  const retargetTopic = useCallback(
    (oldSlug, { slug, title }) => {
      const list = topicsRef.current
      const index = list.findIndex((t) => t.type === 'topic' && t.slug === oldSlug)
      if (index === -1) return null
      const entry = { key: topicKey(slug), type: 'topic', slug, title }
      setTopics(
        list.some((t) => t.key === entry.key)
          ? list.filter((_, i) => i !== index) // already follows the new one
          : list.map((t, i) => (i === index ? entry : t))
      )
      return entry.key
    },
    [setTopics]
  )

  /* Returns true when the source is followed afterwards. */
  const toggleSource = useCallback(
    (slug) => {
      const following = !sourcesRef.current.includes(slug)
      setSources(
        following ? [...sourcesRef.current, slug] : sourcesRef.current.filter((s) => s !== slug)
      )
      return following
    },
    [setSources]
  )

  const value = useMemo(
    () => ({
      topics,
      sources,
      addTopic,
      addKeyword,
      removeTopic,
      restoreTopic,
      retargetTopic,
      toggleSource,
      followedTopic: (slug) => topics.find((t) => t.type === 'topic' && t.slug === slug) || null,
      followedKeyword: (text) => topics.find((t) => norm(t.title) === norm(text)) || null,
      isSourceFollowed: (slug) => sources.includes(slug),
    }),
    [topics, sources, addTopic, addKeyword, removeTopic, restoreTopic, retargetTopic, toggleSource]
  )

  return <FollowContext.Provider value={value}>{children}</FollowContext.Provider>
}

export function useFollow() {
  return useContext(FollowContext)
}

/* What the API needs for "Moje teme": a topic by slug, a keyword by its text. */
export function topicQueryValue(entry) {
  return entry.type === 'topic' ? entry.slug : entry.title
}
